/**
 * 🛡️ useSafeFetch - Hook centralisé pour requêtes fetch robustes
 * 
 * Résout TOUTES les erreurs réseau courantes:
 * - "Failed to execute 'clone' on 'Response'" (rrweb interception)
 * - "Response body is already used" (double lecture)
 * - "Failed to fetch" (AbortController non géré)
 * - Memory leaks sur composants démontés
 * 
 * 🔒 P0 EXECUTION CONTEXT (07/01/2025):
 * Utilise window.fetch (patché par usePageExecutionContext)
 * pour garantir l'injection automatique du header X-Execution-Context-Id
 * 
 * RÈGLES:
 * 1. UN fetch = UN consumer du body (jamais de clone)
 * 2. AbortController obligatoire pour TOUTE requête
 * 3. Cleanup automatique au démontage
 * 4. Fetch patché pour injection du header
 * 
 * @usage
 * const { safeFetch, isAborted } = useSafeFetch();
 * 
 * useEffect(() => {
 *   safeFetch('/api/data')
 *     .then(data => setData(data))
 *     .catch(err => !isAborted(err) && console.error(err));
 * }, []);
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * 🔒 P0 EXECUTION CONTEXT: Utilise window.fetch (patché par usePageExecutionContext)
 * pour garantir l'injection automatique du header
 */
const getContextAwareFetch = () => {
  if (typeof window !== 'undefined') {
    // window.fetch est patché par usePageExecutionContext
    // Le header X-Execution-Context-Id est injecté automatiquement
    return window.fetch.bind(window);
  }
  return fetch;
};

/**
 * Hook principal pour fetch sécurisé avec AbortController automatique
 */
export function useSafeFetch() {
  const abortControllersRef = useRef(new Map());
  const isMountedRef = useRef(true);
  
  // Cleanup au démontage
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      // Annuler TOUTES les requêtes en cours
      abortControllersRef.current.forEach((controller, key) => {
        console.log(`[useSafeFetch] 🧹 Aborting request: ${key}`);
        controller.abort();
      });
      abortControllersRef.current.clear();
    };
  }, []);
  
  // Vérifier si une erreur est une erreur d'abort
  const isAborted = useCallback((error) => {
    return error?.name === 'AbortError' || 
           error?.message === 'Request aborted' ||
           error?.message?.includes('aborted');
  }, []);
  
  // Vérifier si le composant est monté
  const isMounted = useCallback(() => {
    return isMountedRef.current;
  }, []);
  
  /**
   * Fetch sécurisé avec:
   * - Fetch patché (injection header ExecutionContext)
   * - AbortController automatique
   * - Lecture unique du body (pas de clone)
   * - Timeout configurable
   */
  const safeFetch = useCallback(async (url, options = {}) => {
    const {
      timeout = 60000,
      requestId = url,
      ...fetchOptions
    } = options;
    
    // Créer un AbortController pour cette requête
    const controller = new AbortController();
    const requestKey = `${requestId}-${Date.now()}`;
    abortControllersRef.current.set(requestKey, controller);
    
    // Setup timeout
    const timeoutId = setTimeout(() => {
      console.warn(`[useSafeFetch] ⏱️ Request timeout after ${timeout}ms: ${url}`);
      controller.abort();
    }, timeout);
    
    try {
      // 🔒 P0 EXECUTION CONTEXT: Utilise fetch patché
      const contextFetch = getContextAwareFetch();
      
      const response = await contextFetch(url, {
        ...fetchOptions,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // ✅ CRITICAL: Read body as text ONCE (no clone!)
      let responseText;
      try {
        responseText = await response.text();
      } catch (readError) {
        console.error(`[useSafeFetch] Failed to read response body:`, readError);
        throw new Error('Failed to read server response');
      }
      
      // ✅ Parse JSON from text (network already consumed)
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        // Si ce n'est pas du JSON, retourner le texte brut
        if (response.headers.get('content-type')?.includes('text/')) {
          data = responseText;
        } else {
          console.error(`[useSafeFetch] JSON parse error:`, parseError);
          console.error(`[useSafeFetch] Response preview:`, responseText.substring(0, 200));
          throw new Error('Invalid JSON response from server');
        }
      }
      
      // ✅ Check status AFTER parsing
      if (!response.ok) {
        const errorMessage = data?.detail || data?.message || `HTTP ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }
      
      return data;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Vérifier si c'est un abort (ne pas logger comme erreur)
      if (error.name === 'AbortError') {
        console.log(`[useSafeFetch] Request aborted: ${url}`);
        throw error;
      }
      
      // 🔒 P0: Traitement silencieux des erreurs 499 (contexte invalidé)
      if (error.status === 499) {
        console.log(`[useSafeFetch] ⏹️ Context invalidated: ${url}`);
        throw error;
      }
      
      console.error(`[useSafeFetch] Error for ${url}:`, error.message);
      throw error;
      
    } finally {
      // Cleanup
      abortControllersRef.current.delete(requestKey);
    }
  }, []);
  
  /**
   * Annuler toutes les requêtes en cours
   */
  const abortAll = useCallback(() => {
    abortControllersRef.current.forEach((controller, key) => {
      console.log(`[useSafeFetch] 🛑 Manual abort: ${key}`);
      controller.abort();
    });
    abortControllersRef.current.clear();
  }, []);
  
  /**
   * Fetch POST simplifié
   */
  const safePost = useCallback((url, body, options = {}) => {
    return safeFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(body),
      ...options
    });
  }, [safeFetch]);
  
  /**
   * Fetch GET simplifié avec auth auto
   */
  const safeGet = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return safeFetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers
      },
      ...options
    });
  }, [safeFetch]);
  
  return {
    safeFetch,
    safeGet,
    safePost,
    isAborted,
    isMounted,
    abortAll
  };
}

/**
 * Fonction standalone pour usage hors hooks
 * 🔒 P0: Utilise aussi le fetch patché
 * ⚠️ Préférer useSafeFetch dans les composants
 */
export async function safeFetchStandalone(url, options = {}) {
  const {
    timeout = 60000,
    ...fetchOptions
  } = options;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    // 🔒 P0 EXECUTION CONTEXT: Utilise fetch patché
    const contextFetch = getContextAwareFetch();
    
    const response = await contextFetch(url, {
      ...fetchOptions,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // ✅ Read body ONCE
    const responseText = await response.text();
    
    // ✅ Parse from text
    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      data = responseText;
    }
    
    if (!response.ok) {
      const error = new Error(data?.detail || data?.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    
    return data;
    
  } finally {
    clearTimeout(timeoutId);
  }
}

export default useSafeFetch;
